import { useCallback } from "react";

import { AccountViewer } from "components/form/graphql/types";
import useTypedContext from "hooks/useTypedContext";
import { Action, Stack } from "types/generated";
import { AccessResult, NO_ACCESS_MESSAGE, hasAtLeastOneRequiredAction } from "utils/access";
import { AccountContext } from "views/AccountWrapper";

import { checkTemplateDeploymentAccess } from "./helpers/checkTemplateDeploymentAccess";
import useHasStackAdminAccess from "./useHasStackAdminAccess";

// StackManage is the backend's deprecated fallback for the stackDelete mutation.
const DELETE_ACCESS_TYPES = [Action.StackDelete, Action.StackManage, Action.SpaceAdmin];

const hasDeleteAccess = (viewer: AccountViewer, spaceId: string) =>
  hasAtLeastOneRequiredAction(DELETE_ACCESS_TYPES, viewer, spaceId);

/**
 * Hook to determine if the current user can delete a stack
 * Stacks created from a template deployment (blueprintDeployment) cannot be deleted.
 * @returns A function that takes an optional stack parameter and returns an AccessResult
 * indicating whether the user has permission to delete the stack
 */
const useCanDeleteStack = (): ((stack?: Stack) => AccessResult) => {
  const { viewer } = useTypedContext(AccountContext);
  const hasAdminAccess = useHasStackAdminAccess();

  return useCallback(
    (stack?: Stack) => {
      if (!stack) {
        return { result: false };
      }

      const blueprintCheck = checkTemplateDeploymentAccess(stack);
      if (blueprintCheck) {
        return blueprintCheck;
      }

      if (viewer.admin) {
        return { result: true };
      }

      if (hasAdminAccess(stack).result) {
        return { result: true };
      }

      // TODO: remove this condition for space id once we have a proper types for queries results
      if (hasDeleteAccess(viewer, stack.spaceDetails?.id || stack.space)) {
        return { result: true };
      }

      return { result: false, message: NO_ACCESS_MESSAGE };
    },
    [hasAdminAccess, viewer]
  );
};

export default useCanDeleteStack;
